import type {PlaceId} from '@jetbrains/teamcity-ui'
import {pluginRegistry} from '@jetbrains/teamcity-ui'
import {skipToken} from '@reduxjs/toolkit/query'
import * as React from 'react'
import {use, useMemo, useSyncExternalStore} from 'react'

import {EntityContext} from '../../../contexts/entity'
import {stringifyId} from '../../../types'
import {emptyArray} from '../../../utils/empty'
import {useJobId} from '../../pages/PipelinesPages/EditPipelinePage/EditPipelinePage.hooks'

import {usePluginsLoader} from './Plugin.loader'
import {pluginsApi} from './Plugin.requests'
import type {DummyPlugin, PluginLocationContext, Props} from './Plugin.types'
import {getActiveEntitiesFromUrl} from './Plugin.utils'

export const useCachedPlugins = (placeId: PlaceId): ReadonlyArray<string> => {
  const plugins = useSyncExternalStore(
    onStoreChange => pluginRegistry.subscribe(onStoreChange),
    () => {
      const plugins = pluginRegistry.getStore()[placeId]
      if (Array.isArray(plugins)) {
        return plugins
      }
      return emptyArray
    },
  )
  return useMemo(() => plugins.map(plugin => plugin.name), [plugins])
}

export const useActiveEntities = (props: PluginLocationContext): PluginLocationContext => {
  const entityContextValue = use(EntityContext)
  const {
    projectId,
    buildId,
    buildTypeId,
    agentId,
    agentPoolId,
    agentTypeId,
    testRunId,
    pipelineId,
    runId,
  } = {
    ...entityContextValue,
    ...props,
  }
  return React.useMemo(
    () =>
      window.ReactUI.isSakuraUI
        ? {
            projectId,
            buildId: buildId != null ? stringifyId(buildId) : null,
            buildTypeId,
            agentId: agentId != null ? stringifyId(agentId) : null,
            agentPoolId: agentPoolId != null ? stringifyId(agentPoolId) : null,
            agentTypeId: agentTypeId != null ? stringifyId(agentTypeId) : null,
            testRunId,
            pipelineId,
            pipelineRunId: runId,
          }
        : getActiveEntitiesFromUrl(),
    [
      agentId,
      agentPoolId,
      agentTypeId,
      buildId,
      buildTypeId,
      pipelineId,
      projectId,
      runId,
      testRunId,
    ],
  )
}

const useAvailablePlugins = (
  placeId: PlaceId,
  activeEntities: PluginLocationContext,
  disableFetch?: boolean,
): [boolean, ReadonlyArray<DummyPlugin>] => {
  const {isLoading, availablePlugins} = pluginsApi.endpoints.getPlaceIdAvailablePlugins.useQuery(
    disableFetch ? skipToken : {placeId, activeEntities},
    {
      selectFromResult: ({isLoading, data}) => ({
        isLoading,
        availablePlugins: data ?? emptyArray,
      }),
    },
  )
  return [isLoading, availablePlugins]
}

export const usePlugins = ({
  placeId,
  pluginDevelopmentMode,
  disableFetch,
  ...restEntities
}: Props): [PluginLocationContext, ReadonlyArray<string>] => {
  const entities = {...restEntities}
  const jobId = useJobId()
  if (!entities.buildId && jobId != null) {
    /*
    Still no clear understanding of how to throw pipelineId, runId, jobId into the plugin via context.
    Temporary solution is to throw jobId through buildId.
    */
    entities.buildId = jobId
  }
  const activeEntities = useActiveEntities(entities)
  const [availablePluginsLoading, availablePlugins] = useAvailablePlugins(
    placeId,
    activeEntities,
    disableFetch,
  )
  usePluginsLoader({
    availablePlugins,
    availablePluginsLoading,
    activeEntities,
    placeId,
    pluginDevelopmentMode,
  })
  const pluginsToRender = useCachedPlugins(placeId)
  return [activeEntities, pluginsToRender]
}
